import { API_ENDPOINTS } from '../config/api'
import { safeJson } from '../lib/utils'

type ApiResult<T> = { success: boolean; data?: T; message?: string }

async function apiFetch<T>(url: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  })
  const body = (await safeJson(res)) as ApiResult<T> & { message?: string }
  if (!res.ok) throw new Error(body?.message || `Request failed (${res.status})`)
  if (body && typeof body === 'object' && 'success' in body && body.success === false) {
    throw new Error(body.message || 'Request failed')
  }
  return (body?.data ?? body) as T
}

function orgQuery(orgId?: string | null, extra?: Record<string, string | undefined>) {
  const p = new URLSearchParams()
  if (orgId) p.set('organization_id', orgId)
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v != null && v !== '') p.set(k, v)
    }
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export type StandardScope = {
  id: string
  standardCode: string
  applicabilityStatus: string
  reason?: string | null
}

export type ControlLink = {
  id: string
  standardCode: string
  controlId: string
  controlTitle?: string | null
  implementationStatus: string
  ownerText?: string | null
  notes?: string | null
}

export type EvidenceLink = {
  id: string
  evidenceId: string
  controlLinkId?: string | null
  relationshipType: string
  notes?: string | null
  evidence?: {
    id: string
    title: string
    verificationStatus?: string | null
    freshnessStatus?: string | null
    updatedAt?: string
  } | null
}

export type RemediationLink = {
  id: string
  remediationTaskId?: string | null
  recommendationId?: string | null
  notes?: string | null
  remediationTask?: { id: string; title: string; status: string } | null
  recommendation?: { id: string; title: string; status: string; standardCode?: string } | null
}

export type GrcSummaryProjection = {
  applicableStandardsCount: number
  controlsTotal: number
  controlsImplemented: number
  controlsPartial: number
  controlsNotStarted: number
  controlsNotApplicable: number
  controlCoveragePercent: number | null
  evidenceLinkedCount: number
  evidenceVerifiedCount: number | null
  remediationOpenCount: number
  remediationCompletedCount: number
  readinessStatus: string
  explanation: Array<{ code: string; message: string }>
  riskScore: null
  complianceScore: null
  complianceScope?: ComplianceScopeStandard[]
}

export type ComplianceScopeStandard = {
  standardCode: string
  selfAssessedPercent: number | null
  verifiedPercent: number | null
  verifiedStatus?: string | null
  verifiedSource?: string | null
  available?: boolean
}

export type GrcSummaryRow = GrcSummaryProjection & {
  informationSystemId: string
  name: string
  lifecycleStatus?: string | null
  criticality?: string | null
  complianceScope?: ComplianceScopeStandard[]
}

export async function fetchGrcSummaries(
  token: string,
  orgId?: string | null,
  filters?: Record<string, string | undefined>
) {
  return apiFetch<GrcSummaryRow[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS_RISK_COMPLIANCE_SUMMARIES}${orgQuery(orgId, filters)}`,
    token
  )
}

export async function fetchStandardScopes(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<StandardScope[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_STANDARDS(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function createStandardScope(
  token: string,
  systemId: string,
  payload: { standardCode: string; applicabilityStatus?: string; reason?: string },
  orgId?: string | null
) {
  return apiFetch<StandardScope>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_STANDARDS(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify(payload) }
  )
}

export async function deleteStandardScope(token: string, systemId: string, scopeId: string, orgId?: string | null) {
  return apiFetch<{ deleted: boolean }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_STANDARD(systemId, scopeId)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export async function fetchControlLinks(
  token: string,
  systemId: string,
  orgId?: string | null,
  filters?: { standardCode?: string; implementationStatus?: string }
) {
  return apiFetch<ControlLink[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_CONTROLS(systemId)}${orgQuery(orgId, filters)}`,
    token
  )
}

export async function createControlLink(
  token: string,
  systemId: string,
  payload: Record<string, unknown>,
  orgId?: string | null
) {
  return apiFetch<ControlLink>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_CONTROLS(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify(payload) }
  )
}

export async function deleteControlLink(
  token: string,
  systemId: string,
  controlLinkId: string,
  orgId?: string | null
) {
  return apiFetch<{ deleted: boolean }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_CONTROL(systemId, controlLinkId)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export async function fetchEvidenceLinks(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<EvidenceLink[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_EVIDENCE(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function createEvidenceLink(
  token: string,
  systemId: string,
  payload: { evidenceId: string; controlLinkId?: string; relationshipType?: string },
  orgId?: string | null
) {
  return apiFetch<EvidenceLink>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_EVIDENCE(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify(payload) }
  )
}

export async function deleteEvidenceLink(
  token: string,
  systemId: string,
  evidenceLinkId: string,
  orgId?: string | null
) {
  return apiFetch<{ deleted: boolean }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_EVIDENCE_LINK(systemId, evidenceLinkId)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export async function fetchRemediationLinks(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<RemediationLink[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_REMEDIATION(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function createRemediationLink(
  token: string,
  systemId: string,
  payload: { remediationTaskId?: string; recommendationId?: string; controlLinkId?: string },
  orgId?: string | null
) {
  return apiFetch<RemediationLink>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_REMEDIATION(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify(payload) }
  )
}

export async function deleteRemediationLink(
  token: string,
  systemId: string,
  remediationLinkId: string,
  orgId?: string | null
) {
  return apiFetch<{ deleted: boolean }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_REMEDIATION_LINK(systemId, remediationLinkId)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export type OrgEvidenceOption = { id: string; title: string; verificationStatus?: string | null }

export async function fetchOrgEvidenceOptions(token: string): Promise<OrgEvidenceOption[]> {
  try {
    const res = await fetch(API_ENDPOINTS.GOVERNANCE_EVIDENCE, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const body = await safeJson(res)
    if (!res.ok) return []
    const list = Array.isArray(body) ? body : body?.data || body?.evidence || []
    return (list as Array<{ id: string; title: string; verificationStatus?: string }>).map((e) => ({
      id: e.id,
      title: e.title,
      verificationStatus: e.verificationStatus,
    }))
  } catch {
    return []
  }
}

export type OrgRemediationOption = {
  id: string
  title: string
  status: string
  kind: 'task' | 'recommendation'
}

export async function fetchOrgRemediationOptions(token: string): Promise<OrgRemediationOption[]> {
  const options: OrgRemediationOption[] = []
  try {
    const [tasksRes, recRes] = await Promise.all([
      fetch(API_ENDPOINTS.COMPLIANCE_REMEDIATION_TASKS, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(API_ENDPOINTS.COMPLIANCE_RECOMMENDATIONS, { headers: { Authorization: `Bearer ${token}` } }),
    ])
    const tasksBody = await safeJson(tasksRes)
    const recBody = await safeJson(recRes)
    const tasks = Array.isArray(tasksBody) ? tasksBody : tasksBody?.data || tasksBody?.tasks || []
    const recs = Array.isArray(recBody) ? recBody : recBody?.data || recBody?.recommendations || []
    for (const t of tasks as Array<{ id: string; title: string; status: string }>) {
      options.push({ id: t.id, title: t.title, status: t.status, kind: 'task' })
    }
    for (const r of recs as Array<{ id: string; title: string; status: string }>) {
      options.push({ id: r.id, title: r.title, status: r.status, kind: 'recommendation' })
    }
  } catch {
    /* non-blocking */
  }
  return options
}

export async function fetchEnabledStandards(token: string): Promise<string[]> {
  try {
    const res = await fetch(API_ENDPOINTS.COMPLIANCE_STANDARDS_ENABLED, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const body = await safeJson(res)
    if (!res.ok) return []
    const codes: string[] = []
    const enabled = body?.enabledStandards || body?.data?.enabledStandards || body?.standards || body?.data || []
    if (Array.isArray(enabled)) {
      for (const item of enabled) {
        if (typeof item === 'string') codes.push(item)
        else if (item?.code) codes.push(String(item.code))
        else if (item?.standardCode) codes.push(String(item.standardCode))
      }
    }
    return codes
  } catch {
    return []
  }
}
